import Joi from 'joi';

declare module 'joi' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  type VE = import('joi').ValidationError;
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  type VO = import('joi').ValidationOptions;
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  type AVO = import('joi').AsyncValidationOptions;
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  type SM<T> = import('joi').SchemaMap<T>;
  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  type OS<T> = import('joi').ObjectSchema<T>;

  type GenericValidationResult<T> =
    | {
        error: VE;
        errors?: VE;
        warning?: VE;
        value: unknown;
      }
    | {
        error: undefined;
        errors?: VE;
        warning?: VE;
        value: T;
      };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface ObjectSchema<TSchema = any> {
    /**
     * Appends the allowed object keys. If schema is null, undefined, or {}, no changes will be applied.
     */
    append<TAppend = TSchema>(schema?: SM<TAppend>): OS<TSchema & TAppend>;

    /**
     * Sets or extends the allowed object keys.
     */
    keys<TAppend = TSchema>(schema?: SM<TAppend>): OS<TSchema & TAppend>;

    /**
     * Validates a value using the schema and options.
     */
    validate(value: unknown, options?: VO): GenericValidationResult<TSchema>;

    /**
     * Validates a value using the schema and options.
     */
    validateAsync(value: unknown, options?: AVO): Promise<TSchema>;
  }
}

export type DeepPartial<T> = { [P in keyof T]?: DeepPartial<T[P]> };

export type ExtractValues<T> = T extends ReadonlyArray<infer U> ? U : never;

export type AllExcept<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export type SchemaOf<T> = Record<keyof T, Joi.AnySchema | Joi.AnySchema[]>;

export function objectSchemaOf<T>(map: SchemaOf<T>): Joi.ObjectSchema<T> {
  return Joi.object<T>(map);
}

export type StrictSchemaMap<T = {}> = {
  [K in keyof T]: Joi.AnySchema | Joi.AnySchema[];
};

export const isObjectSchema = <T = {}>(
  schema: Joi.AnySchema
): schema is Joi.ObjectSchema<T> => {
  return schema.type === 'object';
};

export const isNumberSchema = (
  schema: Joi.AnySchema
): schema is Joi.NumberSchema => {
  return schema.type === 'number';
};

const makeNumberUnsafe = (schema: Joi.AnySchema) => {
  if (!isNumberSchema(schema)) {
    return schema;
  }
  return schema.unsafe();
};

export const makeAllNumbersUnsafe = <T>(
  input: StrictSchemaMap<T>
): StrictSchemaMap<T> => {
  type KeyValuePairs = Array<[keyof T, Joi.AnySchema | Joi.AnySchema[]]>;
  const entries = Object.entries(input) as KeyValuePairs;
  return entries.reduce(
    (opt, [key, val]) => ({
      ...opt,
      [key]: Array.isArray(val)
        ? val.map((v) => makeNumberUnsafe(v))
        : makeNumberUnsafe(val),
    }),
    {} as StrictSchemaMap<T>
  );
};

export const makeAllOptional = <T>(
  input: StrictSchemaMap<T>
): StrictSchemaMap<T> => {
  type KeyValuePairs = Array<[keyof T, Joi.AnySchema | Joi.AnySchema[]]>;
  const entries = Object.entries(input) as KeyValuePairs;
  return entries.reduce(
    (opt, [key, val]) => ({
      ...opt,
      [key]: Array.isArray(val)
        ? val.map((schema) => schema.optional())
        : val.optional(),
    }),
    {} as StrictSchemaMap<T>
  );
};

export type OptionalKeysOf<T> = Exclude<
  {
    [K in keyof T]: T extends Record<K, T[K]> ? never : K;
  }[keyof T],
  undefined
>;

export const makeAllRequired = <T>(
  input: StrictSchemaMap<T>,
  excluding?: Array<OptionalKeysOf<T>>
): StrictSchemaMap<T> => {
  type Key = OptionalKeysOf<T>;
  type KeyValuePairs = Array<[keyof T, Joi.AnySchema | Joi.AnySchema[]]>;
  const entries = Object.entries(input) as KeyValuePairs;
  const entriesFiltered = excluding
    ? entries.filter(([key]) => !excluding.includes(key as Key))
    : entries;
  return entriesFiltered.reduce(
    (opt, [key, val]) => ({
      ...opt,
      [key]: Array.isArray(val)
        ? val.map((schema) => schema.required())
        : val.required(),
    }),
    {} as StrictSchemaMap<T>
  );
};

export function literalAs<T>(value: T): T {
  return value;
}

export { Joi };
